import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HOTELS_QUERY_KEY, listHotels, type Hotel } from '../features/hotels/api.js';
import {
  PLANS_ORG_QUERY_KEY,
  archivePlan,
  deletePlan,
  listAllPlans,
  updatePlan,
  type MonthlyPlan,
  type MonthlyPlanListItem,
} from '../features/plans/api.js';
import {
  budapestToday,
  countKnownHotels,
  describeLoadFailures,
  describeOrphanedPlans,
  describeSearchUncertainty,
  formatHeaderCounts,
  groupByHotel,
  joinPlansToHotels,
  loadFailureDetail,
  planMatchesSearch,
  resolvePlansListState,
  slotCountsOf,
  topicTitlesOf,
  type ListLoadSubject,
} from '../features/plans/plans-list-model.js';
import { ListLoadError, ListStaleWarning } from '../features/plans/load-error-notice.js';
import { PLAN_STATUS_LABEL, PLAN_STATUS_ORDER } from '../features/plans/plan-status.js';
import { getFavoriteHotels } from '../features/dashboard/api.js';
import type { SlotBucket, SlotCounts } from '@cp/schemas';
import { Button } from '../components/ui/button.js';
import {
  StarIcon,
  ArchiveIcon,
  RotateCcwIcon,
  TrashIcon,
  CheckIcon,
} from '../components/ui/icons.js';

const HU_MONTHS = [
  'január',
  'február',
  'március',
  'április',
  'május',
  'június',
  'július',
  'augusztus',
  'szeptember',
  'október',
  'november',
  'december',
];

// This page no longer maps post statuses to buckets — the API does it in SQL, from
// SLOT_BUCKET_BY_STATUS in @cp/schemas. Only ONE place decides what "done" means,
// which is stronger than two copies that merely start out agreeing. All the web
// still needs is the bucket type, to colour the bar segments.

const BUCKET_COLOR: Record<SlotBucket, string> = {
  todo: 'var(--st-neutral-bg)',
  progress: 'var(--st-topic-pending-bg)',
  review: 'var(--st-content-review-bg)',
  done: 'var(--brand-500)',
};

function relativeHu(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'most';
  if (min < 60) return `${min} perce`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour} órája`;
  const day = Math.floor(hour / 24);
  if (day === 1) return 'tegnap';
  if (day < 7) return `${day} napja`;
  const week = Math.floor(day / 7);
  if (week < 5) return `${week} hete`;
  return new Date(iso).toLocaleDateString('hu-HU');
}

const COLLAPSED_STORAGE_KEY = 'hestia.plans.collapsed-hotels';

/**
 * React Query's `error` is typed `Error | null` but a thrown non-Error still
 * lands there at runtime, so normalise rather than cast.
 */
function asError(isError: boolean, error: unknown): Error | null {
  if (!isError) return null;
  return error instanceof Error ? error : new Error('ismeretlen hiba');
}

function PlansList() {
  const [hotelFilter, setHotelFilter] = useState<string>('');
  const [monthFilter, setMonthFilter] = useState<string>('');
  // Seed status filter from sessionStorage — the dashboard "Tervek
  // állapota" cards drop their status here when they link in, and we
  // consume it once on mount then clear so a manual reload doesn't
  // re-apply yesterday's filter forever.
  const [statusFilter, setStatusFilter] = useState<MonthlyPlan['status'] | ''>(() => {
    if (typeof window === 'undefined') return '';
    const seed = window.sessionStorage.getItem('plans.statusFilterSeed');
    if (seed) window.sessionStorage.removeItem('plans.statusFilterSeed');
    return (seed as MonthlyPlan['status']) ?? '';
  });
  const [query, setQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  // Collapsed-hotel set persists between sessions so the user doesn't
  // have to re-collapse every time. Defaults: everything past the first
  // 3 hotels is collapsed for first-time users.
  const [collapsedHotels, setCollapsedHotels] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set();
    try {
      const raw = window.localStorage.getItem(COLLAPSED_STORAGE_KEY);
      if (!raw) return new Set();
      return new Set(JSON.parse(raw) as string[]);
    } catch {
      return new Set();
    }
  });
  // Used as a one-shot flag — once we've applied the "first 3 open"
  // default, we don't keep re-collapsing whatever the user expanded.
  const [defaultsApplied, setDefaultsApplied] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(COLLAPSED_STORAGE_KEY) != null;
  });
  // Europe/Budapest, not UTC: the expired-plan cut-off must flip when the
  // Hungarian day flips, not up to two hours earlier.
  const todayIso = budapestToday(new Date());

  const qc = useQueryClient();
  const hotelsQ = useQuery<Hotel[]>({ queryKey: HOTELS_QUERY_KEY, queryFn: listHotels });
  const hotels = hotelsQ.data ?? [];
  const favoritesQ = useQuery<string[]>({
    queryKey: ['dashboard-favorites'],
    queryFn: getFavoriteHotels,
  });

  // ONE org-wide request instead of one per hotel. The per-hotel fan-out put N
  // requests into a single per-handler throttler bucket, which tripped 429 on its
  // own and then got multiplied by React Query's default retry. Sharing
  // PLANS_ORG_QUERY_KEY with the dashboard and the command palette means a
  // navigation between pages hits the cache, and the throwing queryFn means a
  // /plans failure shows the error banner here instead of an empty list.
  const plansQ = useQuery<MonthlyPlanListItem[]>({
    queryKey: PLANS_ORG_QUERY_KEY,
    queryFn: listAllPlans,
    staleTime: 30_000,
  });

  // Retries are modelled as mutations so "a retry is running" means exactly
  // "the user clicked retry and it hasn't finished". Deriving it from
  // `isFetching` made the button announce a retry during any background
  // refetch, including ones nobody asked for.
  const retryLists = useMutation({
    mutationFn: (subjects: ListLoadSubject[]) =>
      Promise.all(
        subjects.map((s) =>
          qc.refetchQueries({
            queryKey: s === 'hotels' ? HOTELS_QUERY_KEY : PLANS_ORG_QUERY_KEY,
          }),
        ),
      ),
  });

  // Pair the org-wide plans with their hotels client-side. `orphanedPlanCount`
  // should always be 0 (both endpoints are org-scoped); it is surfaced rather
  // than assumed so a mismatch can't silently drop a plan.
  const { paired: allPlans, orphanedPlanCount } = useMemo(
    () => joinPlansToHotels(plansQ.data ?? [], hotels),
    [plansQ.data, hotels],
  );

  // Available month filters derived from the actual data.
  const monthOptions = useMemo(() => {
    const set = new Set<string>();
    for (const { plan } of allPlans) set.add(`${plan.year}-${String(plan.month).padStart(2, '0')}`);
    return Array.from(set).sort().reverse();
  }, [allPlans]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Topic titles now ride along with the plan row. Fails open when they're
    // absent — see planMatchesSearch — so missing data can't silently filter a
    // plan out of the results.
    const matchesQuery = (plan: MonthlyPlanListItem, hotelName: string): boolean =>
      planMatchesSearch({
        query: q,
        hotelName,
        topicTitles: topicTitlesOf(plan) ?? [],
        topicsUnknown: topicTitlesOf(plan) === null,
      });
    return allPlans
      .filter(({ plan }) => {
        if (showArchived) return true;
        if (plan.status === 'archived') return false;
        const endIso = plan.endDate ?? `${plan.year}-${String(plan.month).padStart(2, '0')}-31`;
        return endIso >= todayIso;
      })
      .filter(({ hotel }) => !hotelFilter || hotel.id === hotelFilter)
      .filter(
        ({ plan }) =>
          !monthFilter || `${plan.year}-${String(plan.month).padStart(2, '0')}` === monthFilter,
      )
      .filter(({ plan }) => !statusFilter || plan.status === statusFilter)
      .filter(({ plan, hotel }) => matchesQuery(plan, hotel.name))
      .sort((a, b) => b.plan.updatedAt.localeCompare(a.plan.updatedAt));
  }, [allPlans, hotelFilter, monthFilter, statusFilter, query, showArchived, todayIso]);

  // Group filtered plans by hotel for the collapsible-section layout.
  // Favorite hotels come first, then the rest — both groups sorted
  // alphabetically by hotel name (Hungarian locale, accent-aware).
  const grouped = useMemo(
    () => groupByHotel({ filtered, favorites: favoritesQ.data ?? [] }),
    [filtered, favoritesQ.data],
  );

  const headerCounts = useMemo(
    () =>
      formatHeaderCounts({
        shownHotels: grouped.length,
        totalHotels: countKnownHotels(allPlans),
        shownPlans: filtered.length,
        totalPlans: allPlans.length,
      }),
    [grouped.length, allPlans, filtered.length],
  );

  // How many of the plans we're showing could not actually be searched, because
  // their topic data didn't arrive. planMatchesSearch keeps them in the results;
  // this is what tells the user the results are not fully verified.
  const searchUncertainty = useMemo(
    () =>
      describeSearchUncertainty({
        query: query.trim(),
        uncertainCount: filtered.filter(({ plan }) => topicTitlesOf(plan) === null).length,
      }),
    [query, filtered],
  );

  // Decides loading / error / empty / list. Crucially this is what stops a failed
  // hotel or plan query from falling through to the "Nincs ilyen terv." empty state.
  const listState = resolvePlansListState({
    hotelsLoading: hotelsQ.isLoading,
    hotelsError: asError(hotelsQ.isError, hotelsQ.error),
    hotelCount: hotels.length,
    plansLoading: plansQ.isLoading,
    plansError: asError(plansQ.isError, plansQ.error),
    planCount: plansQ.data?.length ?? 0,
    groupCount: grouped.length,
  });

  const failedSubjects = listState.failures.map((f) => f.subject);
  const retryFailed = () => retryLists.mutate(failedSubjects);
  const failureSubjectText = describeLoadFailures(listState.failures);
  const failureDetail = loadFailureDetail(listState.failures);
  const orphanNotice = describeOrphanedPlans(orphanedPlanCount);

  // Name the culprit only when we can: with no other filter active, the
  // difference is the archive/expired cut-off. Otherwise stay generic rather
  // than blaming the wrong control.
  const hidingReason =
    !showArchived && !hotelFilter && !monthFilter && !statusFilter && !query.trim()
      ? 'archív és lejárt rejtve'
      : 'szűrve';

  // One-shot: when the user first lands on the page, collapse everything
  // past the top 3 most-active hotels. After that any toggle is theirs
  // to keep.
  useEffect(() => {
    if (defaultsApplied) return;
    if (grouped.length === 0) return;
    const next = new Set<string>();
    grouped.slice(3).forEach((g) => next.add(g.hotel.id));
    setCollapsedHotels(next);
    setDefaultsApplied(true);
  }, [defaultsApplied, grouped]);

  // Persist collapse state across reloads.
  useEffect(() => {
    if (!defaultsApplied) return;
    try {
      window.localStorage.setItem(
        COLLAPSED_STORAGE_KEY,
        JSON.stringify(Array.from(collapsedHotels)),
      );
    } catch {
      /* ignore quota errors */
    }
  }, [collapsedHotels, defaultsApplied]);

  function toggleHotel(hotelId: string) {
    setCollapsedHotels((prev) => {
      const next = new Set(prev);
      if (next.has(hotelId)) next.delete(hotelId);
      else next.add(hotelId);
      return next;
    });
  }

  function expandAll() {
    setCollapsedHotels(new Set());
  }
  function collapseAll() {
    setCollapsedHotels(new Set(grouped.map((g) => g.hotel.id)));
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="h2-eyebrow">Tartalom-naptár</div>
          <h1 className="mt-1.5 text-h1 font-medium leading-none tracking-tight text-[var(--ink-700)]">
            Tervek
          </h1>
        </div>
        <Link to="/app/plans/new" search={{}}>
          <Button>+ Új terv</Button>
        </Link>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Hotel név vagy téma headline…"
          className="h-8 w-[180px] rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] px-3 text-body outline-none placeholder:text-[var(--fg-faint)] focus:border-[var(--brand-500)]"
        />
        <FilterSelect value={hotelFilter} onChange={setHotelFilter} placeholder="Minden hotel">
          {hotels.map((h) => (
            <option key={h.id} value={h.id}>
              {h.name}
              {!h.isActive ? ' (inaktív)' : ''}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect value={monthFilter} onChange={setMonthFilter} placeholder="Minden hónap">
          {monthOptions.map((ym) => (
            <option key={ym} value={ym}>
              {formatMonthLabel(ym)}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as MonthlyPlan['status'] | '')}
          placeholder="Minden állapot"
        >
          {PLAN_STATUS_ORDER.map((s) => (
            <option key={s} value={s}>
              {PLAN_STATUS_LABEL[s]}
            </option>
          ))}
        </FilterSelect>
        <button
          type="button"
          onClick={() => setShowArchived((v) => !v)}
          className={`h-8 rounded-full border px-3 text-meta font-medium transition-colors ${
            showArchived
              ? 'border-[var(--brand-500)] bg-[var(--brand-50)] text-[var(--brand-800)]'
              : 'border-[var(--app-divider)] bg-[var(--app-surface)] text-[var(--fg-2)] hover:bg-[var(--app-hover)]'
          }`}
        >
          {showArchived ? (
            <span className="inline-flex items-center gap-1">
              <CheckIcon size={12} /> Archív látható
            </span>
          ) : (
            'Archív rejtve'
          )}
        </button>
        {grouped.length > 1 && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={expandAll}
              className="h-8 rounded-full border border-[var(--app-divider)] bg-[var(--app-surface)] px-3 text-meta font-medium text-[var(--fg-2)] hover:bg-[var(--app-hover)]"
            >
              Összes kibont
            </button>
            <button
              type="button"
              onClick={collapseAll}
              className="h-8 rounded-full border border-[var(--app-divider)] bg-[var(--app-surface)] px-3 text-meta font-medium text-[var(--fg-2)] hover:bg-[var(--app-hover)]"
            >
              Összes csuk
            </button>
          </div>
        )}
        <div
          className="ml-auto text-meta text-[var(--fg-faint)]"
          data-testid="plans-header-counts"
          title={
            headerCounts.hiding
              ? 'A szűrők (köztük az „Archív rejtve” gomb) elrejtenek adatot — a második szám a teljes darabszám.'
              : undefined
          }
        >
          {headerCounts.label}
          {headerCounts.hiding && (
            <span className="ml-1 text-[var(--fg-faint)]">({hidingReason})</span>
          )}
        </div>
      </div>

      {/* An active search couldn't be evaluated against every plan. The affected
          plans stay in the results (planMatchesSearch fails open) — this says so,
          instead of letting the user assume the result set is verified. */}
      {searchUncertainty && (
        <p
          data-testid="search-uncertainty"
          className="rounded-md border border-[var(--app-divider)] bg-[var(--app-surface-2)] px-3 py-2 text-meta text-[var(--fg-2)]"
        >
          {searchUncertainty}
        </p>
      )}

      {/* A stale-but-usable list keeps rendering; the refetch failure rides along
          as a warning instead of blanking out working content. */}
      {listState.kind !== 'error' && failureSubjectText && (
        <ListStaleWarning
          subjects={failureSubjectText}
          detail={failureDetail}
          onRetry={retryFailed}
          retrying={retryLists.isPending}
        />
      )}

      {/* Plans we received but could not attach to any known hotel. Always 0 in
          practice — surfaced instead of dropped, see joinPlansToHotels. */}
      {orphanNotice && (
        <p
          data-testid="orphaned-plans"
          className="rounded-md border border-[var(--app-divider)] bg-[var(--app-surface-2)] px-3 py-2 text-meta text-[var(--fg-2)]"
        >
          {orphanNotice}
        </p>
      )}

      {/* List grouped by hotel */}
      {listState.kind === 'loading' ? (
        <p className="text-body text-[var(--fg-2)]">Betöltés…</p>
      ) : listState.kind === 'error' ? (
        <ListLoadError
          subjects={failureSubjectText ?? 'az adatokat'}
          detail={failureDetail}
          onRetry={retryFailed}
          retrying={retryLists.isPending}
        />
      ) : listState.kind === 'empty' ? (
        <div className="rounded-lg border border-dashed border-[var(--app-divider)] py-12 text-center">
          <p className="text-body text-[var(--fg-2)]">Nincs ilyen terv.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {grouped.map(({ hotel, plans }) => {
            const collapsed = collapsedHotels.has(hotel.id);
            const isFavorite = (favoritesQ.data ?? []).includes(hotel.id);
            return (
              <HotelSection
                key={hotel.id}
                hotel={hotel}
                plans={plans}
                collapsed={collapsed}
                onToggle={() => toggleHotel(hotel.id)}
                isFavorite={isFavorite}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* HOTEL SECTION — one collapsible block per hotel                    */
/* ------------------------------------------------------------------ */

function HotelSection({
  hotel,
  plans,
  collapsed,
  onToggle,
  isFavorite,
}: {
  hotel: Hotel;
  plans: MonthlyPlanListItem[];
  collapsed: boolean;
  onToggle: () => void;
  isFavorite: boolean;
}) {
  // Aggregate slot stats across all the hotel's plans for the header chips, so a
  // quick glance shows "X total / Y done" without expanding. Summed from the
  // server-side tallies now; plans whose tallies didn't arrive are counted
  // separately so the totals aren't quietly understated.
  const stats = useMemo(() => {
    let total = 0;
    let done = 0;
    let unknownPlans = 0;
    for (const p of plans) {
      const counts = slotCountsOf(p);
      if (counts === null) {
        unknownPlans++;
        continue;
      }
      total += counts.total;
      done += counts.done;
    }
    return { total, done, unknownPlans };
  }, [plans]);

  return (
    <div className="overflow-hidden rounded-lg border border-[var(--app-divider)] bg-[var(--app-surface)]">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[var(--app-hover)]"
      >
        {/* Cover thumb */}
        <div
          className="h-10 w-12 shrink-0 rounded-md border border-[var(--app-divider)] bg-[var(--app-bg)]"
          style={{
            backgroundImage: hotel.coverImageUrl ? `url('${hotel.coverImageUrl}')` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {isFavorite && (
              <span
                className="inline-flex h-[18px] items-center justify-center rounded-full bg-[var(--accent-highlight)] px-1.5 text-meta font-bold text-[var(--ink-900)]"
                aria-label="Kedvenc hotel"
                title="Kedvenc — a listák elején jelenik meg"
              >
                <StarIcon size={12} filled />
              </span>
            )}
            <span className="truncate text-body font-semibold text-[var(--ink-700)]">
              {hotel.name}
            </span>
            {!hotel.isActive && (
              <span className="inline-flex items-center rounded-pill bg-[var(--app-divider)] px-1.5 py-0.5 text-meta font-bold uppercase tracking-wider text-[var(--fg-2)]">
                Inaktív
              </span>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-meta text-[var(--fg-faint)]">
            <span className="tn">{plans.length} terv</span>
            {stats.total > 0 && (
              <>
                <span>·</span>
                <span className="font-mono tabular-nums">
                  {stats.done}/{stats.total} slot kész
                </span>
              </>
            )}
            {stats.unknownPlans > 0 && (
              <>
                <span>·</span>
                <span title="Néhány terv slot-adata nem töltődött be">
                  {stats.unknownPlans} terv slot-adata hiányzik
                </span>
              </>
            )}
          </div>
        </div>
        <span
          className={`text-[var(--fg-2)] transition-transform ${collapsed ? '' : 'rotate-180'}`}
          aria-hidden="true"
        >
          ▾
        </span>
      </button>

      {!collapsed && plans.length > 0 && (
        <div className="border-t border-[var(--app-divider)]">
          <div className="grid grid-cols-[1fr_140px_90px_240px_120px_36px] items-center gap-4 bg-[var(--app-bg)] px-5 py-2 text-meta font-semibold uppercase tracking-wider text-[var(--fg-faint)]">
            <span>Hónap</span>
            <span>Időszak</span>
            <span>Slot-ok</span>
            <span>Állapot</span>
            <span>Utolsó</span>
            <span />
          </div>
          <ul className="divide-y divide-[var(--app-divider)]">
            {plans.map((plan) => {
              return (
                <PlanRow
                  key={plan.id}
                  plan={plan}
                  hotel={hotel}
                  slotCounts={slotCountsOf(plan)}
                  variant="grouped"
                />
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function FilterSelect({
  value,
  onChange,
  placeholder,
  children,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  children: React.ReactNode;
}) {
  const active = !!value;
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`h-8 rounded-full border px-3 text-meta font-medium transition-colors ${
        active
          ? 'border-[var(--brand-500)] bg-[var(--brand-50)] text-[var(--brand-800)]'
          : 'border-[var(--app-divider)] bg-[var(--app-surface)] text-[var(--fg-2)] hover:bg-[var(--app-hover)]'
      }`}
    >
      <option value="">{placeholder}</option>
      {children}
    </select>
  );
}

function formatMonthLabel(ym: string): string {
  const [y, m] = ym.split('-');
  const monthIdx = Number(m) - 1;
  return `${y}. ${HU_MONTHS[monthIdx] ?? m}`;
}

function PlanRow({
  plan,
  hotel,
  slotCounts,
  variant = 'flat',
}: {
  plan: MonthlyPlan;
  hotel: Hotel;
  /**
   * Server-side slot tallies, or null when the payload didn't carry them. Null
   * means "unknown" and renders "—/—" — never a confident 0.
   */
  slotCounts: SlotCounts | null;
  /** `flat` = first column is hotel; `grouped` = hotel is implied by
   * the parent section, first column shows the month name instead. */
  variant?: 'flat' | 'grouped';
}) {
  const qc = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // The action menu is rendered position:fixed (anchored to the trigger's
  // click rect) so it escapes the group card's overflow-hidden clip — without
  // this the menu is cut off on the last row and on short (1–2 plan) lists.
  // Close on scroll/resize since fixed coords don't follow the moving button.
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [menuOpen]);

  // The four bar segments come straight from the aggregate now. `Record<SlotBucket,
  // number>` is exactly what BucketBar wants, and SlotCounts is that plus `total`.
  const buckets: Record<SlotBucket, number> = slotCounts ?? {
    todo: 0,
    progress: 0,
    review: 0,
    done: 0,
  };
  const total = slotCounts?.total ?? 0;
  const done = buckets.done;
  const postsUnknown = slotCounts === null;
  const isArchived = plan.status === 'archived';

  const invalidate = () => {
    // Prefix key: refreshes BOTH the org-wide ['plans','org'] list this page now
    // reads and any ['plans', hotelId] cache other pages hold. Scoping this to
    // ['plans', hotel.id] would have left the bulk list stale, so an archive or
    // delete wouldn't show up here.
    void qc.invalidateQueries({ queryKey: ['plans'] });
  };

  const archive = useMutation({
    mutationFn: () => archivePlan(plan.id),
    onSuccess: () => {
      setMenuOpen(false);
      invalidate();
    },
  });
  const unarchive = useMutation({
    mutationFn: () => updatePlan(plan.id, { status: 'draft' }),
    onSuccess: () => {
      setMenuOpen(false);
      invalidate();
    },
  });
  const del = useMutation({
    mutationFn: () => deletePlan(plan.id),
    onSuccess: () => {
      setMenuOpen(false);
      setConfirmDelete(false);
      invalidate();
    },
  });

  return (
    <li
      className={`relative grid grid-cols-[1fr_140px_90px_240px_120px_36px] items-center gap-4 px-5 py-3.5 transition-colors hover:bg-[var(--app-hover)] ${isArchived ? 'opacity-70' : ''}`}
    >
      {/* Whole row except the action cell links to the plan calendar. We use
          a stretched absolute Link instead of wrapping the cells so the ⋮
          button isn't a click-bubble target. */}
      <Link
        to="/app/plans/$hotelId/$year/$month"
        params={{ hotelId: hotel.id, year: String(plan.year), month: String(plan.month) }}
        className="absolute inset-0"
        aria-label={`${hotel.name} · ${plan.year}. ${HU_MONTHS[plan.month - 1] ?? plan.month}`}
      />

      {variant === 'flat' ? (
        <>
          {/* Hotel: cover thumb + name + location */}
          <div className="relative flex items-center gap-3 min-w-0 pointer-events-none">
            <div
              className="h-10 w-12 shrink-0 rounded-md border border-[var(--app-divider)] bg-[var(--app-bg)]"
              style={{
                backgroundImage: hotel.coverImageUrl ? `url('${hotel.coverImageUrl}')` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <div className="min-w-0">
              <div className="truncate text-body font-semibold text-[var(--ink-700)]">
                {hotel.name}
              </div>
              <div className="truncate text-meta text-[var(--fg-faint)]">
                {hotel.websiteUrl
                  ? new URL(hotel.websiteUrl).host.replace(/^www\./, '')
                  : hotel.slug}
              </div>
            </div>
          </div>

          {/* Month */}
          <div className="relative text-body text-[var(--ink-700)] pointer-events-none">
            {plan.year}. {HU_MONTHS[plan.month - 1] ?? plan.month}
          </div>
        </>
      ) : (
        <>
          {/* Grouped variant: hotel implied, lead column is the month
              name set in display-serif so each row reads as a chapter. */}
          <div className="relative pointer-events-none">
            <div className="text-body font-semibold text-[var(--ink-700)]">
              {HU_MONTHS[plan.month - 1] ?? plan.month}
            </div>
            <div className="text-meta text-[var(--fg-faint)] tn">{plan.year}</div>
          </div>

          {/* Date range */}
          <div className="relative font-mono text-meta text-[var(--fg-2)] pointer-events-none">
            {plan.startDate && plan.endDate ? (
              <>
                <div>{plan.startDate.slice(5)}</div>
                <div className="text-[var(--fg-faint)]">{plan.endDate.slice(5)}</div>
              </>
            ) : (
              <div className="text-[var(--fg-faint)]">—</div>
            )}
          </div>
        </>
      )}

      {/* Slots: done/total. An unknown count renders as "—/—" instead of "0/—",
          which would claim the plan has no finished slots. */}
      <div className="relative font-mono text-body pointer-events-none">
        {postsUnknown ? (
          <span
            className="text-[var(--fg-faint)]"
            title="A slot-adat nem töltődött be — a szám nem ismert"
            data-testid="slots-unknown"
          >
            —/—
          </span>
        ) : (
          <>
            <span className="font-semibold text-[var(--ink-700)]">{done}</span>
            <span className="text-[var(--fg-faint)]">/{total || '—'}</span>
          </>
        )}
      </div>

      {/* Status: segmented progress bar + archived chip */}
      <div className="relative flex items-center gap-2 pointer-events-none">
        {isArchived ? (
          <span className="inline-flex items-center rounded-full bg-[var(--app-divider)] px-2 py-0.5 text-meta font-semibold uppercase tracking-wider text-[var(--fg-2)]">
            Archivált
          </span>
        ) : (
          <BucketBar buckets={buckets} total={total} />
        )}
      </div>

      {/* Updated */}
      <div className="relative text-meta text-[var(--fg-faint)] pointer-events-none">
        {relativeHu(plan.updatedAt)}
      </div>

      {/* Action menu — sits on top of the stretched link via z-index. */}
      <div className="relative z-10 flex justify-end">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (menuOpen) {
              setMenuOpen(false);
              return;
            }
            const r = e.currentTarget.getBoundingClientRect();
            const MENU_W = 176; // w-44
            const MENU_H = 92; // ~2 action rows
            const openUp = r.bottom + MENU_H + 8 > window.innerHeight;
            setMenuPos({
              top: openUp ? r.top - MENU_H - 4 : r.bottom + 4,
              left: Math.max(8, r.right - MENU_W),
            });
            setMenuOpen(true);
          }}
          aria-label="Műveletek"
          title="Műveletek"
          className="flex h-10 w-10 items-center justify-center rounded-md border border-transparent text-[var(--fg-muted)] hover:border-[var(--app-divider)] hover:bg-[var(--app-surface-2)] hover:text-[var(--ink-700)]"
        >
          <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <circle cx="5" cy="12" r="1.75" />
            <circle cx="12" cy="12" r="1.75" />
            <circle cx="19" cy="12" r="1.75" />
          </svg>
        </button>
        {menuOpen && menuPos && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(false);
              }}
            />
            {/* position:fixed at the trigger's viewport rect so it escapes the
                group card's overflow-hidden (which clips the menu on the last
                row and short lists). Right edge anchored to the button; flips
                above when near the viewport bottom. */}
            <div
              className="fixed z-50 w-44 overflow-hidden rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] shadow-lg"
              style={{ top: menuPos.top, left: menuPos.left }}
            >
              {!isArchived ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    archive.mutate();
                  }}
                  disabled={archive.isPending}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-meta text-[var(--ink-700)] hover:bg-[var(--app-hover)] disabled:opacity-60"
                >
                  <ArchiveIcon size={14} /> Archiválás
                </button>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    unarchive.mutate();
                  }}
                  disabled={unarchive.isPending}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-meta text-[var(--ink-700)] hover:bg-[var(--app-hover)] disabled:opacity-60"
                >
                  <RotateCcwIcon size={14} /> Visszaállítás
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDelete(true);
                  setMenuOpen(false);
                }}
                className="flex w-full items-center gap-2 border-t border-[var(--app-divider)] px-3 py-2 text-left text-meta text-[var(--danger)] hover:bg-[var(--st-rejected-bg)]"
              >
                <TrashIcon size={14} /> Törlés
              </button>
            </div>
          </>
        )}
      </div>

      {confirmDelete && (
        <DeletePlanDialog
          plan={plan}
          hotelName={hotel.name}
          postCount={total}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => del.mutate()}
          pending={del.isPending}
          error={del.error as Error | null}
        />
      )}
    </li>
  );
}

function DeletePlanDialog({
  plan,
  hotelName,
  postCount,
  onCancel,
  onConfirm,
  pending,
  error,
}: {
  plan: MonthlyPlan;
  hotelName: string;
  postCount: number;
  onCancel: () => void;
  onConfirm: () => void;
  pending: boolean;
  error: Error | null;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-lg bg-[var(--app-surface)] p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-h2 font-semibold text-[var(--danger)]">Tervet törlése?</h3>
        <p className="mt-2 text-body text-[var(--ink-700)]">
          <strong>{hotelName}</strong> · {plan.year}. {HU_MONTHS[plan.month - 1] ?? plan.month}
        </p>
        <p className="mt-1.5 text-meta text-[var(--fg-2)]">
          Ez véglegesen eltávolítja a tervet és minden hozzá kötött adatot ({postCount} poszt,
          témajavaslatok, tartalmak, képek, kommentek, jóváhagyási csomagok). Az archiválás
          kíméletesebb alternatíva.
        </p>
        {error && <p className="mt-2 text-meta text-[var(--danger)]">{error.message}</p>}
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-9 items-center rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] px-4 text-body font-medium text-[var(--ink-700)] hover:bg-[var(--app-hover)]"
          >
            Mégsem
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className="inline-flex h-9 items-center rounded-md bg-[var(--danger)] px-4 text-body font-semibold text-white hover:opacity-90 disabled:opacity-60"
          >
            {pending ? 'Törlés…' : 'Igen, törlés'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BucketBar({ buckets, total }: { buckets: Record<SlotBucket, number>; total: number }) {
  if (total === 0) {
    return <div className="h-1.5 rounded-full bg-[var(--app-divider)]" aria-label="Nincs slot" />;
  }
  const order: SlotBucket[] = ['todo', 'progress', 'review', 'done'];
  return (
    <div className="flex h-1.5 overflow-hidden rounded-full bg-[var(--app-divider)]">
      {order.map((b) => {
        const w = (buckets[b] / total) * 100;
        if (w === 0) return null;
        return (
          <div
            key={b}
            style={{ width: `${w}%`, backgroundColor: BUCKET_COLOR[b] }}
            aria-label={`${b}: ${buckets[b]}/${total}`}
          />
        );
      })}
    </div>
  );
}

export const Route = createFileRoute('/app/plans/')({
  component: PlansList,
});
